import React, { useMemo } from 'react';
import cx from 'classnames';

import IconButton from '../../../../../../../../common/buttons/IconButton';
import Checkbox from '../../../../../../../../common/data-entry/InputSelectors/Checkbox';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import useAppInfo from '../../../../../../../../common/hooks/useAppInfo';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { COMMENT_FIELD_IS_NOT_AVAILABLE } from '../../../../../../../../constants/notificationMessages';
import { scBlue100 } from '../../../../../../../../constants/systemColors';
import { validateQuestion } from '../../../../../services/questionUtils';
import CustomFieldConfiguration from '../../../CustomFieldConfiguration/CustomFieldConfiguration';
import EditFormFields from '../../../EditFormFields';
import { customClassObject } from '../../customClassObject';
import FieldPreview from '../CustomFieldsGenerator/FieldPreview';
import {
  FINANCE_COLUMN,
  FIXED_FIELD_COLUMN,
  INVESTIGATOR_FIELD_COLUMN,
  ITEM_SEQUENCE_COLUMN,
  LOG_VIEW_COLUMN,
  UPDATED_CDASH_ITEMS_COLUMN
} from '../FieldsToSelect/gridService';

import { EditFormAnswerView } from './EditFormAnswerView';
import { OptionalCheckbox } from './OptionalCheckbox';

export function OptionalField({
  item,
  index,
  formLabelData,
  labelIndex,
  getCheckboxForTableView,
  getInvestigatorFieldCheckboxForTableView,
  getExpenseFixedCheckboxForTableView,
  onChangeLabelTextHandler,
  inputs,
  previewDataList,
  updateInputsAndPreviewData,
  editMode,
  terminologyVersionGroupNumber,
  domain,
  domainConditionalLogic,
  updateState,
  columnsToShow,
  onFinanceFieldChange
}) {
  item.isChecked = item.isChecked ? item.isChecked : false; /* TODO: dangerous data mutation */
  const { isChecked, updatedQuestion, sequence, label, isExtensible, inputType, decode, question } = item;
  const appInfo = useAppInfo();
  const itemGroupFinanceEventEnabled = appInfo?.features?.itemGroupFinanceEventEnabled;

  const { uniqueId, optionalTableChckUniqId, fixedFieldChckUuid, financeUuid } = useMemo(function() {
    const uniqueId = crypto.randomUUID();
    return {
      uniqueId,
      optionalTableChckUniqId: `table${uniqueId}`,
      fixedFieldChckUuid: `table${uniqueId}fixed`,
      financeUuid: `table${uniqueId}finance`
    };
  }, []);

  return (
    <div key={index} className="igs-grid-row-inner">
      <div className="igs-grid-row-column" data-column="CDASH_ITEMS">
        {!item?.mandatoryField && (
          <div className="igs-grid-row-column-cdash-items-actions">
            <IconButton
              className="glyphicon glyphicon-edit mr-2"
              suit="glyphicon"
              color="blue"
              title="Edit CDASH Item"
              onClick={() => onEditFormFields(item, labelIndex, index, 'optional')}
            >
              edit
            </IconButton>
            <OptionalCheckbox
              isChecked={isChecked}
              uniqueId={uniqueId}
              item={item}
              labelIndex={labelIndex}
              disabled={item?.mandatoryField}
              index={index}
              inputs={inputs}
              decode={decode}
              updateInputsAndPreviewData={updateInputsAndPreviewData}
            />
          </div>
        )}
        {inputType !== 'reminder' && (
          <div
            data-testid="cdash-field-name"
            className={cx('igs-grid-row-column-cdash-items-question', { 'overflow-ellipsis': domain !== 'ICF' })}
          >
            {domain !== 'ICF' ? question : updatedQuestion || question}
          </div>
        )}
      </div>
      {columnsToShow.includes(FINANCE_COLUMN) && (
        <div className="igs-grid-row-column" data-column="FINANCE">
          {item.inputType === 'radio' && itemGroupFinanceEventEnabled && (
            <Checkbox
              color={scBlue100}
              checked={!!item.financeField}
              className="p-0 overflow-ellipsis"
              id={financeUuid}
              onChange={({ target }) => {
                onFinanceFieldChange(target?.checked, item, 0, labelIndex, index, 'optional');
              }}
            />
          )}
        </div>
      )}
      <div className="igs-grid-row-column" data-column="CDASH">
        {' '}
        {item?.cdashAliasName}
      </div>
      {columnsToShow.includes(UPDATED_CDASH_ITEMS_COLUMN) && (
        <div className="igs-grid-row-column" data-column="UPDATED_CDASH_ITEMS">
          <input
            className="form-control select-width"
            value={(item.hasOwnProperty('updatedQuestion') && updatedQuestion) || ''}
            type="text"
            disabled
          />
        </div>
      )}
      {inputType !== 'checkbox' ? (
        <div className="igs-grid-row-column" data-column="ANSWER_TYPE">
          <FieldPreview
            withoutWrapper
            labelText={label}
            data={item}
            formId={inputs[0].formId}
            formLabelId={formLabelData.formLableId}
            customClassObject={customClassObject}
            itemDefIndex={index}
            onChangeLabelTextHandler={e => onChangeLabelTextHandler(e.target.value, labelIndex, index, false)}
          />
        </div>
      ) : (
        <div className="igs-grid-row-column" />
      )}

      <div className="igs-grid-row-column" data-column="ITEM_SEQUENCE">
        {/* Edit button with an option to select predefined items for dropdown.
                    Exist only in optional AccordionItem */}
        <EditFormAnswerView
          isExtensible={isExtensible}
          updateInputsAndPreviewData={updateInputsAndPreviewData}
          item={item}
          labelIndex={labelIndex}
          index={index}
          inputs={inputs}
          terminologyVersionGroupNumber={terminologyVersionGroupNumber}
          editMode={editMode}
        />
        {!item?.mandatoryField && (
          <CustomFieldConfiguration
            domainConditionalLogic={domainConditionalLogic}
            item={item}
            allInputs={inputs}
            onChange={() => updateState(updateInputsAndPreviewData(inputs))}
          />
        )}
        {/* Item Sequence block similar can be found in getCustomField */}
        {columnsToShow.includes(ITEM_SEQUENCE_COLUMN) && (
          <>
            <i className={`material-icons formseqicon`} title="Form View">
              library_books
            </i>
            <input
              type="text"
              readOnly
              value={sequence ? Number(sequence) : ''}
              title="Form Item Sequence"
              className="form-control igs-grid-sequence-field"
            />
          </>
        )}
      </div>
      {columnsToShow.includes(FIXED_FIELD_COLUMN) && (
        <div className="igs-grid-row-column" data-column="FIXED_FIELD">
          {getExpenseFixedCheckboxForTableView(item, fixedFieldChckUuid, labelIndex, index, 'optional')}
        </div>
      )}
      {columnsToShow.includes(INVESTIGATOR_FIELD_COLUMN) && (
        <div className="igs-grid-row-column" data-column="INVESTIGATOR_FIELD">
          {!isICFAutoPopulatedField(domain, item) &&
            getInvestigatorFieldCheckboxForTableView(item, labelIndex, index, 'optional', domain)}
        </div>
      )}
      {columnsToShow.includes(LOG_VIEW_COLUMN) && (
        <div className="igs-grid-row-column" data-column="LOG_VIEW">
          {getCheckboxForTableView(item, optionalTableChckUniqId, labelIndex, index, 'optional')}
        </div>
      )}
    </div>
  );

  function onEditFormFields(item, labelIndex, questionIndex, type) {
    const modalBox = ModalBoxes.open({
      component: (
        <EditFormFields
          onSave={data => {
            const questionName = data.updatedQuestionFieldName;
            const validationMessage = validateQuestion(inputs, questionName);
            if (validationMessage) {
              if (validationMessage === COMMENT_FIELD_IS_NOT_AVAILABLE) {
                NotificationManager.error(validationMessage);
                return;
              }
              NotificationManager.warning(validationMessage);
            }
            if (data) {
              type === 'optional'
                ? (inputs[0][type][labelIndex].itemDefinitionList[questionIndex].updatedQuestion =
                    data.updatedQuestionFieldName)
                : (inputs[0][type][labelIndex].customItemDefinationList[questionIndex].updatedQuestion =
                    data.updatedQuestionFieldName);
              updateInputsAndPreviewData(inputs);
              modalBox.close();
            }
          }}
          itemDefinationData={item}
          fullData={previewDataList}
        />
      )
    });
  }
}

function isICFAutoPopulatedField(domain, item) {
  return domain === 'ICF' && ['ICF IRB Approval Date', 'ICF Version Number'].includes(item.name);
}
